import { createContext, useContext } from "react"
import {
  type CombinedLoanAndProductsQuery,
  type LoanApplicationInput,
} from "@/generated/graphql"
import { useBoolVariation } from "@/hooks/launchDarklyHooks"

export type LoanApplication = NonNullable<
  CombinedLoanAndProductsQuery["loanApplication"]
>

export interface LoanContextProps {
  loanId: string

  loanApplication: LoanApplication
  capitalProducts: CombinedLoanAndProductsQuery["capitalProducts"]

  isLoading: boolean
  isError: boolean
  experiment: "Control" | "A"
  userHasRewards: boolean
  cashToCloseDisplaysAsRange: boolean
  loanDashboardEnabled: boolean

  update: (updates: Partial<LoanApplicationInput>) => void
  clear: () => void
  save: () => Promise<void>

  refetch: () => Promise<void>
}

export const LoanContext = createContext<LoanContextProps>(
  {} as LoanContextProps
)

export const useLoanContext = (): LoanContextProps => {
  const context = useContext(LoanContext)
  const loanDashboardEnabled = useBoolVariation(
    "enable-loan-dashboard-v0",
    false
  )
  if (context === undefined) {
    throw new Error("useLoanContext must be used within the app")
  }

  const cashToCloseDisplaysAsRange: boolean =
    !context.userHasRewards && context.loanApplication?.loanProduct !== "refi"

  return {
    ...context,
    cashToCloseDisplaysAsRange,
    loanDashboardEnabled,
  }
}

export interface PlaceData {
  placeId: string
  address: string
}

export interface NewLoanContextProps {
  loanId?: string
  setLoanId: (loanId: string) => void
  setPlace: (place: PlaceData) => void
  place?: PlaceData
  placeLoadError?: string
}

export const NewLoanContext = createContext<NewLoanContextProps>(
  {} as NewLoanContextProps
)

export const useNewLoanContext = (): NewLoanContextProps => {
  return useContext(NewLoanContext)
}
